import {Dialog} from '@wandb/components';
import {Button} from '@wandb/weave/components';
import {WorkspaceTag} from '@wandb/weave/components/Tag/WorkspaceTag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import React from 'react';

import type * as FilterTypes from '../../util/filterTypes';
import type * as RunTypes from '../../util/runTypes';
import {type WBTableRowWithRunSetInfo} from '../../util/tablecols';
import {RunTagInfiniteDropdown} from '../InfiniteScrollDropdown/RunTagComponents';
import {useRunTagEditor} from './hooks/useRunTagEditor';
import {useRunSelectorFeatureDisabled} from './RunSelectorFeaturesContext';

type AddFilterFn = (
  key: RunTypes.Key,
  op: FilterTypes.ValueOp,
  value: RunTypes.Value
) => void;

type RenderTagsCellProps = {
  entityName: string;
  isGroup: boolean;
  isGroupRun: boolean;
  isHoveringRow: boolean;
  onAddFilter?: AddFilterFn;
  projectName: string;
  row: WBTableRowWithRunSetInfo;
};

export function RunSelectorTagCell({
  entityName,
  isGroup,
  isGroupRun,
  isHoveringRow,
  onAddFilter,
  projectName,
  row,
}: RenderTagsCellProps) {
  const isDisabled = useRunSelectorFeatureDisabled();
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const {addTag, deleteTag} = useRunTagEditor({
    entityName,
    isGroup,
    projectName,
    row,
  });

  if (isGroup && !isGroupRun) {
    return <></>;
  }

  const isTagsReadOnly = isDisabled('tag');
  const isFiltersReadOnly = isDisabled('filter');

  return (
    <Tailwind>
      <div data-test="icon-wrap" className="flex items-center gap-4">
        {row.tags.map((tag, index) => (
          <WorkspaceTag
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            key={index}
            tag={tag}
            style={{
              cursor: isTagsReadOnly ? 'unset' : 'pointer',
            }}
            onClick={() => {
              if (!isFiltersReadOnly && onAddFilter) {
                onAddFilter({section: 'tags', name: tag.name}, '=', true);
              }
            }}
            onRemoveClick={
              isTagsReadOnly
                ? undefined
                : (e: React.SyntheticEvent) => {
                    e.stopPropagation();
                    deleteTag(tag.name);
                  }
            }
          />
        ))}
        {!isTagsReadOnly && (
          <>
            <Button
              className={clsx('tag-add-button-icon-only', {
                hidden: !isHoveringRow && !isOpen,
              })}
              data-test="tag-add-button"
              onClick={() => setIsOpen(open => !open)}
              ref={triggerRef}
              variant="ghost"
              size="small"
              icon="add-new"
              aria-label="Add a new tag"
              tooltip="Add a new tag"
            />
            <Dialog.Popover
              isOpen={isOpen}
              onOpenChange={setIsOpen}
              triggerRef={triggerRef}
              placement="bottom start"
              aria-label="Add a new tag">
              <Tailwind>
                <RunTagInfiniteDropdown
                  entityName={entityName}
                  projectName={projectName}
                  isMultiSelect
                  toggleSelection={async tag => {
                    for (const t of row.tags) {
                      if (t.name === tag) {
                        deleteTag(tag);
                        return;
                      }
                    }
                    addTag(tag);
                    setIsOpen(false);
                  }}
                  selections={row.tags.map(t => t.name)}
                />
              </Tailwind>
            </Dialog.Popover>
          </>
        )}
      </div>
    </Tailwind>
  );
}
